import type { ChangeEvent } from 'react';
import { Stack } from '@mui/material';
import { Select, TextField } from '@travelity/form';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DiscountType } from '@travelity/api';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/contexts/user';
import type { PackageBookingFormValues } from '../../package-form.helpers';

interface PackageBookingDiscountProps {
    maxAbsoluteDiscount?: number;
}

const PackageBookingDiscount = ({
    maxAbsoluteDiscount,
}: PackageBookingDiscountProps) => {
    const { t } = useTranslation('product');
    const { user } = useUserContext();
    const { control, setValue } = useFormContext<PackageBookingFormValues>();
    const discountType = useWatch({
        control,
        name: 'discount.type',
    });
    const discountAmount = useWatch({
        control,
        name: 'discount.amount',
    });
    const previousDiscountTypeRef = useRef(discountType);

    const discountTypeOptions = useMemo(
        () => [
            { value: 'relative', label: '%' },
            {
                value: 'absolute',
                label: user?.currency || 'Absolute',
            },
        ],
        [user?.currency]
    );

    useEffect(() => {
        if (previousDiscountTypeRef.current === discountType) {
            return;
        }

        previousDiscountTypeRef.current = discountType;
        setValue('discount.amount', 0, {
            shouldDirty: true,
        });
    }, [discountType, setValue]);

    useEffect(() => {
        if (
            discountType !== DiscountType.ABSOLUTE ||
            maxAbsoluteDiscount === undefined ||
            discountAmount === undefined ||
            discountAmount <= maxAbsoluteDiscount
        ) {
            return;
        }

        setValue('discount.amount', maxAbsoluteDiscount, {
            shouldDirty: true,
        });
    }, [discountAmount, discountType, maxAbsoluteDiscount, setValue]);

    const handleDiscountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const digitsOnly = raw.replace(/\D/g, '');
        const nextValue = digitsOnly === '' ? 0 : Number(digitsOnly);

        if (discountType === DiscountType.RELATIVE) {
            setValue('discount.amount', Math.min(100, Math.max(0, nextValue)), {
                shouldDirty: true,
            });
            return;
        }

        setValue(
            'discount.amount',
            maxAbsoluteDiscount !== undefined
                ? Math.min(maxAbsoluteDiscount, Math.max(0, nextValue))
                : Math.max(0, nextValue),
            {
                shouldDirty: true,
            }
        );
    };

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                bgcolor: '#FFFFFF',
                borderRadius: '8px',
            }}
        >
            <TextField
                label={t('Discount')}
                placeholder={t('Type Discount')}
                name="discount.amount"
                type="number"
                onChange={handleDiscountChange}
                inputProps={{
                    min: 0,
                    max:
                        discountType === DiscountType.RELATIVE
                            ? 100
                            : maxAbsoluteDiscount,
                }}
                sx={{ flexGrow: 2 }}
            />
            <Select
                width="140px"
                name="discount.type"
                label=""
                options={discountTypeOptions}
            />
        </Stack>
    );
};

export default PackageBookingDiscount;
